import { formatSearchValue } from '@/components/ActiveSearchTags/helpers';
import {
  ActiveSearchTagsOptions,
  SearchValueShow,
} from '@/components/ActiveSearchTags/types';
import { useGlobalContainerRef } from '@/wc/helper/ref';
import { Tag, Tooltip } from '@moneyforward/ap-frontend-components';
import classnames from 'classnames/bind';
import { CSSProperties, useMemo } from 'react';
import styles from './ActiveSearchValues.module.scss';

const cx = classnames.bind(styles);

const DEFAULT_MAX_VISIBLE_TAGS = 4;

const defaultOverflowLabel = (count: number) => `他${count}件`;

type ActiveSearchValuesProps = {
  activeSearchValues: SearchValueShow[];
  options?: ActiveSearchTagsOptions;
};

/**
 * Tooltip content component for remaining tags
 */
const RemainingTagsTooltip = ({
  remainingTags,
}: {
  remainingTags: SearchValueShow[];
}) => (
  <ul className={cx('tooltip-item', 'tooltip-list')}>
    {remainingTags.map((searchValue) => {
      const displayValue = formatSearchValue(
        searchValue.valueShow,
        searchValue?.concatChar
      );
      return (
        <li key={searchValue.fieldLabel}>
          {searchValue.fieldLabel}：{displayValue}
        </li>
      );
    })}
  </ul>
);

/**
 * Reusable search tag component
 */
const SearchTag = ({
  searchValue,
  index,
  container,
  maxWidth,
  children,
}: {
  searchValue: SearchValueShow;
  index: number;
  container: () => HTMLElement;
  maxWidth?: string | number;
  children: React.ReactNode;
}) => {
  const displayValue = formatSearchValue(
    searchValue.valueShow,
    searchValue?.concatChar
  );
  const expandFully = searchValue.expandFully === true;
  let style: CSSProperties | undefined;
  if (!expandFully && maxWidth === 'none') {
    style = { maxWidth };
  } else if (!expandFully && maxWidth !== undefined) {
    style = { maxWidth, minWidth: 0 };
  }
  const tag = (
    <span
      className={cx('tag-container', {
        'tag-container-expanded': expandFully,
      })}
      style={style}
    >
      <Tag
        className={cx('search-tag')}
        closable={false}
        size='small'
        status='success'
      >
        <span
          className={cx('tag-content', {
            'tag-content-expanded': expandFully,
          })}
        >
          {children}
        </span>
      </Tag>
    </span>
  );

  if (expandFully) return tag;

  return (
    <Tooltip
      key={`${searchValue.fieldLabel}-${index}`}
      title={
        <div
          className={cx('tooltip-item')}
        >{`${searchValue.fieldLabel}：${displayValue}`}</div>
      }
      overlayClassName={cx('tooltip-container')}
      getPopupContainer={container}
    >
      {tag}
    </Tooltip>
  );
};

/**
 * Component to display active search values as tags
 * Shows the search criteria that are currently being applied
 * Shows up to four tags unless the caller passes a different visible count.
 */
export const ActiveSearchTags = ({
  activeSearchValues,
  options,
}: ActiveSearchValuesProps) => {
  const container = useGlobalContainerRef();
  const maxVisible = options?.maxVisible ?? DEFAULT_MAX_VISIBLE_TAGS;
  const heading = options?.heading ?? '検索結果に反映中';
  const overflowLabel = options?.overflowLabel ?? defaultOverflowLabel;

  // Filter out empty values and limit to visible tags
  const validSearchValues = useMemo(() => {
    if (!activeSearchValues || activeSearchValues.length === 0) {
      return [];
    }
    return activeSearchValues.filter((searchValue) => {
      const displayValue = formatSearchValue(
        searchValue.valueShow,
        searchValue?.concatChar
      );
      return displayValue.trim();
    });
  }, [activeSearchValues]);

  const { visibleTags, remainingTags, remainingCount } = useMemo(() => {
    const visible = validSearchValues.slice(0, maxVisible);
    const remaining = validSearchValues.slice(maxVisible);
    return {
      visibleTags: visible,
      remainingTags: remaining,
      remainingCount: remaining.length,
    };
  }, [maxVisible, validSearchValues]);

  // Early return if no active search values
  if (!activeSearchValues || validSearchValues.length === 0) {
    return null;
  }

  return (
    <div className={cx('search-results-reflection')}>
      <span className={cx('label-text')}>{heading}</span>

      {visibleTags.map((searchValue, index) => {
        const displayValue = formatSearchValue(
          searchValue.valueShow,
          searchValue?.concatChar
        );
        const maxWidth =
          searchValue.expandFully || !options?.tagMaxWidth
            ? undefined
            : options.tagMaxWidth(visibleTags.length) ?? 'none';

        return (
          <SearchTag
            key={`${searchValue.fieldLabel}-${index}`}
            searchValue={searchValue}
            index={index}
            container={container}
            maxWidth={maxWidth}
          >
            {`${searchValue.fieldLabel}：${displayValue}`}
          </SearchTag>
        );
      })}

      {remainingCount > 0 && (
        <Tooltip
          title={<RemainingTagsTooltip remainingTags={remainingTags} />}
          overlayClassName={cx('tooltip-container')}
          getPopupContainer={container}
        >
          <span className={cx('tag-container')}>
            <Tag
              className={cx('search-tag')}
              closable={false}
              status='success'
              size='small'
            >
              <span className={cx('tag-content')}>
                {overflowLabel(remainingCount)}
              </span>
            </Tag>
          </span>
        </Tooltip>
      )}
    </div>
  );
};
